import { useEffect, useState } from "react";
import { FileCheck2, TrendingUp, UserRoundCog } from "lucide-react";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs/tabs.tsx";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import type { PageProps } from "../../types/page.ts";
import {
  AssignManagerTab,
  DocumentApprovalTab,
  EmployeeProgressionHero,
  EMPLOYEE_PROGRESSION_TABS,
  PromotionManagementTab,
  TAB_ASSIGN_MANAGER,
  TAB_DOCUMENT_APPROVAL,
  TAB_PROMOTION_MANAGEMENT,
  useEmployeeProgressionData,
  type EmployeeProgressionTabId,
} from "../../components/features/employee-progression/index.ts";
import { showError } from "../../utils/toast.ts";
import styles from "./employee-progression-page.module.css";

const TAB_ICONS: Record<EmployeeProgressionTabId, typeof UserRoundCog> = {
  "assign-manager": UserRoundCog,
  "promotion-management": TrendingUp,
  "document-approval": FileCheck2,
};

export default function EmployeeProgressionPage({
  title,
  description,
  pageMeta,
}: PageProps) {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [activeTab, setActiveTab] =
    useState<EmployeeProgressionTabId>(TAB_ASSIGN_MANAGER);
  const {
    employees,
    employeeOptions,
    departments,
    loading,
    error,
    refetch,
    fetchDesignations,
  } = useEmployeeProgressionData();

  const pageTitle = pageMeta?.title || title || "Employee Progression";
  const pageDescription =
    pageMeta?.description ||
    description ||
    "Manage employee progression workflows including manager assignment, promotions, and document approvals.";

const heroTitle = "Employee Workflow Management";

const heroDescription =
  "Streamline reporting and leave manager assignments, promotion workflows, and document approvals from a single workspace.";


  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [pageTitle, setBreadcrumbs]);

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  return (
    <PageShell>
<EmployeeProgressionHero
  title={heroTitle}
  description={heroDescription}
/>

      <Card className={styles.tableCard} padding="5xl">
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            setActiveTab(value as EmployeeProgressionTabId)
          }
          idPrefix="employee-progression-tabs"
          variant="card"
        >
          <TabsList className={styles.tabsList}>
            {EMPLOYEE_PROGRESSION_TABS.map((tab) => {
              const Icon = TAB_ICONS[tab.id];
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className={styles.tabTrigger}
                >
                  <span className={styles.tabTriggerInner}>
                    <Icon size={16} aria-hidden />
                    {tab.label}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value={TAB_ASSIGN_MANAGER} className={styles.tabPanel}>
            <AssignManagerTab
              employeeOptions={employeeOptions}
              loadingEmployees={loading}
            />
          </TabsContent>

          <TabsContent
            value={TAB_PROMOTION_MANAGEMENT}
            className={styles.tabPanel}
          >
            <PromotionManagementTab
              employees={employees}
              employeeOptions={employeeOptions}
              departments={departments}
              loadingEmployees={loading}
              fetchDesignations={fetchDesignations}
              onPromotionComplete={refetch}
            />
          </TabsContent>

          <TabsContent value={TAB_DOCUMENT_APPROVAL} className={styles.tabPanel}>
            <DocumentApprovalTab />
          </TabsContent>
        </Tabs>
      </Card>
    </PageShell>
  );
}

